/**
 * Cockpit-camera placement: where the pilot's eye sits inside the airframe,
 * and how a held look-around key swings the view. Kept pure like
 * `camera-follow.ts`'s chase/fly-by math, so it is testable without a
 * renderer and `flight-scene.ts` only has to call it.
 *
 * ## Eye position — now the real per-aircraft `.TBL` crew position
 *
 * A model's baked LOD (`data/baked/models/<KEY>.json`) carries a `spheres`
 * array — `.TBL` `type = 1` "shaded sphere" nodes (`docs/formats/models-tbl.md`
 * §4.3, VERIFIED against `RB.EXE`'s own handler), which on every flyable
 * scout is the pilot's head (`FOKD7`'s single sphere sits at the cockpit,
 * `colour = 36` = a leather flying-helmet brown). `packages/baker/src/
 * cockpit-eye.ts` (`cockpitEyeOffsetFromLod`) turns that sphere plus the
 * LOD's own bounding box into exactly the `forwardM`/`upM`/`rightM` triple
 * this file needs: `aircraft-mesh.ts` recentres a loaded mesh onto its
 * bounding-box centre before it reaches world space, and the sim's own
 * `pose.position` is that recentred origin (`flight-scene.ts`'s `applyPose`),
 * so the eye offset is the sphere's position *relative to that same centre*,
 * not its raw `.TBL` coordinate — see `cockpit-eye.ts`'s header for the full
 * derivation, including why no axis-rotation correction is needed despite the
 * model's Z-up bake and the −90° render-time pitch.
 *
 * `EYE_OFFSETS` below is computed offline this way for all 22 flyable scouts
 * (the only aircraft with a cockpit view at all — `cockpit-composition.ts`'s
 * `FALLBACK_AIRCRAFT` note) and checked in as a plain table, the same shape
 * `flight/baked-models.ts`'s `BAKED_MODEL_IDS` uses for the same reason: a
 * pure function here must not read `data/` at import time.
 * `apps/web/test/cockpit-view.test.ts` recomputes every entry from the real
 * bake, read-only, so the table can never silently drift from what
 * `cockpit-eye.ts` would derive today. `DEFAULT_EYE_OFFSET` is now only the
 * fallback for the AI-only two-seaters/bombers nobody flies from the cockpit
 * — still an estimate, since none of them has a baked crew sphere to read.
 */

import { addVec3, BODY_FORWARD, BODY_RIGHT, BODY_UP, multiplyQuat, quatFromAxisAngle, rotateVec3, scaleVec3, type Pose, type Quat, type Vec3 } from "@red-opus/engine";
import type { GlanceDirection } from "./keys.ts";

export interface EyeOffset {
  /** Metres forward of the aircraft's pose origin, along `BODY_FORWARD`. */
  readonly forwardM: number;
  /** Metres above the pose origin, along `BODY_UP`. */
  readonly upM: number;
  /** Metres right of the pose origin, along `BODY_RIGHT`. 0 for every seat on the aircraft's centreline. */
  readonly rightM: number;
}

/**
 * A pilot sits close to the wing root, eyes a little above the fuselage's
 * centreline — small enough numbers that getting them exactly right per
 * aircraft matters far less than getting the *frame* (recentred mesh, engine
 * Y-up convention) right, which is why one shared estimate is good enough
 * here (INFERRED, port-side estimate — see the module doc above for why a
 * real per-aircraft table isn't available).
 */
export const DEFAULT_EYE_OFFSET: EyeOffset = { forwardM: 0.3, upM: 0.55, rightM: 0 };

/**
 * Keyed by `AircraftKey` (e.g. `"CAMEL"`); falls back to `DEFAULT_EYE_OFFSET`
 * for every airframe not listed (the AI-only two-seaters/bombers). `rightM`
 * is `0` on all 22 — every one of them carries its crew sphere on the
 * fuselage centreline, which is what a single-seat scout's cockpit should
 * do. See this module's header and `packages/baker/src/cockpit-eye.ts` for
 * how these numbers are derived; `cockpit-view.test.ts` pins them against
 * the real bake.
 */
const EYE_OFFSETS: Readonly<Record<string, EyeOffset>> = {
  MORANE: { forwardM: 0.746, upM: 0.785, rightM: 0 },
  FOKE3: { forwardM: 1.423, upM: 0.785, rightM: 0 },
  AIRCO: { forwardM: 1.854, upM: 0.631, rightM: 0 },
  NIEU11: { forwardM: 1.308, upM: 0.885, rightM: 0 },
  NIEUPORT: { forwardM: 1.454, upM: 0.854, rightM: 0 },
  SOPPUP: { forwardM: 1.1, upM: 0.7, rightM: 0 },
  SPAD_7: { forwardM: 1.246, upM: 0.646, rightM: 0 },
  ALBA_D2: { forwardM: 0.777, upM: 0.962, rightM: 0 },
  SOPTRI: { forwardM: 1.346, upM: 0.592, rightM: 0 },
  ALBA_D3: { forwardM: 0.777, upM: 0.962, rightM: 0 },
  HALBD2: { forwardM: 1.615, upM: 0.808, rightM: 0 },
  SE5: { forwardM: 0.646, upM: 0.554, rightM: 0 },
  ALBA_D5: { forwardM: 0.777, upM: 0.962, rightM: 0 },
  CAMEL: { forwardM: 1.438, upM: 0.885, rightM: 0 },
  PFALZ: { forwardM: 0.908, upM: 1.054, rightM: 0 },
  FOKDR1: { forwardM: 1.108, upM: 0.546, rightM: 0 },
  SPAD_13: { forwardM: 1.246, upM: 0.646, rightM: 0 },
  NIEU28: { forwardM: 1.4, upM: 0.992, rightM: 0 },
  FOKD7: { forwardM: 0.762, upM: 0.762, rightM: 0 },
  SIEMENS: { forwardM: 1.077, upM: 1, rightM: 0 },
  SNIPE: { forwardM: 1.131, upM: 0.885, rightM: 0 },
  FOKD8: { forwardM: 1.062, upM: 0.877, rightM: 0 },
};

export function eyeOffsetFor(aircraftKey: string | undefined): EyeOffset {
  if (!aircraftKey) return DEFAULT_EYE_OFFSET;
  return EYE_OFFSETS[aircraftKey] ?? DEFAULT_EYE_OFFSET;
}

/**
 * Minimum height the pilot's eye is ever placed above the ground under it.
 *
 * Off the ground this never binds. On it, three things can put the eye below
 * datum even though the aeroplane's own origin is a wheel's height above it: a
 * folded undercarriage drops the origin to `gearCollapsedHeightFraction` of its
 * stance, the eye offset is applied through the *rolled* attitude so a wing-down
 * arrival swings it sideways and down, and a crash leaves the airframe at
 * whatever attitude it stopped at. A camera below the ground plane sees the
 * world from underneath, which is the one thing a cockpit view must never do.
 */
export const MIN_EYE_HEIGHT_M = 0.45;

/**
 * World-space position of the pilot's eye for a given pose and offset, never
 * below `groundHeightM + MIN_EYE_HEIGHT_M`.
 *
 * `groundHeightM` is the terrain under the aircraft — `readout.groundHeightM`,
 * which is 0 on this port's flat world and whatever the elevation lane's
 * `GroundQuery` says once that lands.
 */
export function cockpitEyePosition(
  pose: Pose,
  eyeOffset: EyeOffset = DEFAULT_EYE_OFFSET,
  groundHeightM = 0,
): Vec3 {
  const local = addVec3(
    scaleVec3(BODY_FORWARD, eyeOffset.forwardM),
    addVec3(scaleVec3(BODY_UP, eyeOffset.upM), scaleVec3(BODY_RIGHT, eyeOffset.rightM)),
  );
  const eye = addVec3(pose.position, rotateVec3(pose.orientation, local));
  const floor = groundHeightM + MIN_EYE_HEIGHT_M;
  return eye.y >= floor ? eye : { x: eye.x, y: floor, z: eye.z };
}

/**
 * Fixed snap-view angles, body-relative (degrees). `back` is a full reversal
 * rather than ±180 either way since the sign doesn't matter at exactly π. The
 * six directions are the original's own `F1`–`F6` cockpit views (§ `keys.ts`'s
 * `LOOK_LEFT_KEY` doc); the *angles* are this port's, since the card names the
 * views and not the degrees.
 *
 * They are also the **calibration for right-mouse free look**, which is the
 * port's own addition and has no original to check against: `free-look.ts`
 * imports `GLANCE_PITCH_DEG`'s two vertical extremes as its own pitch stops, so
 * a drag can never see higher or further over the side than a snap view does.
 * Its yaw stop is deliberately tighter than `back`'s reversal — see that file.
 */
export const GLANCE_YAW_DEG: Readonly<Record<GlanceDirection, number>> = {
  forward: 0,
  left: -90,
  right: 90,
  back: 180,
  up: 0,
  down: 0,
};

/**
 * `down` is steep on purpose and stops short of straight down: it is the
 * over-the-side look a pilot took to judge a bomb release, so it has to clear
 * the coaming and the lower wing while still keeping some horizon in frame to
 * fly on. Straight down (−90) would be a floor camera with nothing to level
 * against.
 */
export const GLANCE_PITCH_DEG: Readonly<Record<GlanceDirection, number>> = {
  forward: 0,
  left: 0,
  right: 0,
  back: 0,
  up: 35,
  down: -55,
};

/**
 * Yaw turns about the body's *down* axis, not up — matching
 * `packages/engine`'s own internal `WORLD_YAW_AXIS` convention
 * (`math3d.ts`, not exported) so that a positive angle turns the nose right,
 * the same sense `camera-follow.test.ts` already pins for heading (heading
 * 90° points east). Confirmed by hand with the Rodrigues formula: rotating
 * `BODY_FORWARD` by +90° about this axis lands on `BODY_RIGHT`.
 */
const YAW_AXIS: Vec3 = { x: 0, y: -1, z: 0 };

/**
 * Where the pilot's head is pointed relative to the airframe's own forward
 * axis, in degrees. The six glances above are six fixed values of this; right
 * mouse free look (`free-look.ts`) is the continuum between them.
 */
export interface LookAim {
  readonly yawDeg: number;
  readonly pitchDeg: number;
}

/** Dead ahead — the aim of the `forward` glance, and of a cockpit nobody is looking around in. */
export const LOOK_AHEAD: LookAim = { yawDeg: 0, pitchDeg: 0 };

/**
 * The look-direction quaternion for an aim, in the aircraft's own body frame
 * (before `pose.orientation` is applied). `multiplyQuat(a, b)` applies `b`
 * first, so this composes as pitch-then-yaw.
 *
 * Order matters here in a way it never did for the glance table alone — no
 * listed glance combines a nonzero yaw with a nonzero pitch, but a free-look
 * drag combines them constantly. Pitch-then-yaw is the one that behaves like a
 * head: the pitch is taken about the *seat's* right-hand axis and the yaw about
 * the seat's own vertical, so looking 90° left and then up tips the view
 * towards the top wing rather than rolling it about the line of sight.
 */
export function lookRotation(aim: LookAim): Quat {
  const yawRad = (aim.yawDeg * Math.PI) / 180;
  const pitchRad = (aim.pitchDeg * Math.PI) / 180;
  return multiplyQuat(quatFromAxisAngle(YAW_AXIS, yawRad), quatFromAxisAngle(BODY_RIGHT, pitchRad));
}

/** The look-direction quaternion for one of the six fixed glances. */
export function glanceRotation(glance: GlanceDirection): Quat {
  return lookRotation({ yawDeg: GLANCE_YAW_DEG[glance], pitchDeg: GLANCE_PITCH_DEG[glance] });
}

export interface CockpitCameraView {
  readonly position: Vec3;
  readonly lookAt: Vec3;
  /**
   * The aircraft's own up vector, for `camera.up` — a cockpit view has to
   * roll with the airframe (unlike the chase camera, which deliberately
   * stays world-up per `camera-follow.ts`'s header note: you're sitting
   * inside this one). Independent of `glance`: turning your head doesn't
   * tilt your inner ear.
   */
  readonly up: Vec3;
}

/**
 * The eye's world position, a point ahead of it along the looked-at direction,
 * and the aircraft's own up vector — everything `flight-scene.ts` needs to
 * point a `THREE.Camera` (`position` + `lookAt` + `up`).
 *
 * `aim` is right-mouse free look's offset (`free-look.ts`), composed *on top
 * of* the glance rather than replacing it here. In practice the two are never
 * both set: `scene-loop.ts` resolves the precedence once, at the point it reads
 * both devices, and forces the glance to `forward` for as long as a free look
 * is live. Composing rather than choosing keeps this function a plain
 * concatenation of rotations with no hidden rule of its own — and means the
 * `/assets` viewer and the tests that pass a glance alone are unaffected.
 */
export function cockpitCameraView(
  pose: Pose,
  glance: GlanceDirection,
  eyeOffset: EyeOffset = DEFAULT_EYE_OFFSET,
  groundHeightM = 0,
  aim: LookAim = LOOK_AHEAD,
): CockpitCameraView {
  const position = cockpitEyePosition(pose, eyeOffset, groundHeightM);
  const localLook = rotateVec3(multiplyQuat(glanceRotation(glance), lookRotation(aim)), BODY_FORWARD);
  const worldLook = rotateVec3(pose.orientation, localLook);
  return { position, lookAt: addVec3(position, worldLook), up: rotateVec3(pose.orientation, BODY_UP) };
}
